import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ActiveSubscription } from '~types/subscription/subscription.types';

interface SubscriptionContextType {
  activePlan: ActiveSubscription | null;
  isLoading: boolean;
  setActivePlan: (plan: ActiveSubscription | null) => void;
  setIsLoading: (loading: boolean) => void;
  hasActivePlan: boolean;
  clearActivePlan: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [activePlan, setActivePlanState] = useState<ActiveSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const setActivePlan = useCallback((plan: ActiveSubscription | null) => {
    setActivePlanState(plan);
  }, []);

  const clearActivePlan = useCallback(() => {
    setActivePlanState(null);
  }, []);

  const hasActivePlan = activePlan !== null && activePlan?.status === 'active';

  const value: SubscriptionContextType = React.useMemo(
    () => ({
      activePlan,
      isLoading,
      setActivePlan,
      setIsLoading,
      hasActivePlan,
      clearActivePlan,
    }),
    [activePlan, isLoading, setActivePlan, setIsLoading, hasActivePlan, clearActivePlan],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
